// Note that this is temporary middleware and shouldn't be replicated!
// This will soon be replaced by https://github.com/zapier/zapier/pull/22105
// or a similar PR.

import _ from 'lodash';
import { routeTo } from 'redux-router-kit';

import * as Account from 'app/entities/Account';
import * as ToastNotification from 'app/entities/ToastNotification';
import ActionTypes from 'app/common/apollo/ActionTypes';
import FlowActions from 'app/beta-editor/FlowActions';
import { FOLDER_ID_PARAM } from 'app/dashboard/utils/constants';
import { getJson } from 'app/common/fetch';
import { getParam } from 'app/common/utils/RouterSelectors';

const operations = {
  fullFolders: {
    errorMessage: 'Couldn’t fetch folders.',
    sideEffects: [
      async (action, store) => {
        const state = store.getState();
        const folderId = getParam(FOLDER_ID_PARAM, state);
        // If we're not routed to a particular folder, nothing to do here.
        if (!folderId) {
          return;
        }
        try {
          // If we _are_ routed to a particular folder, attempt to load it,
          // check its `account_id` vs the `currentAccountId`, and if they're
          // different, then switch to the account to which the folder in the
          // URL belongs.
          const currentAccountId = Account.selectors.all.currentAccountId(
            state
          );
          const folder = await getJson(`/api/v3/folders/${folderId}`);
          if (folder.account_id !== currentAccountId) {
            store.dispatch(Account.actions.switchAccount(folder.account_id));
          }
        } catch (e) {
          // Do nothing. This isn't catastrophic, just an annoyance.
        }
      },
    ],
  },
  createFolder: {
    errorMessage: 'Couldn’t create folder.',
  },
  deleteFolder: {
    errorMessage: 'Couldn’t delete folder.',
    sideEffects: [
      (action, store) => {
        const state = store.getState();
        const folderId = parseInt(getParam(FOLDER_ID_PARAM, state), 10);
        const deletedFolderId = parseInt(
          _.get(action, ['data', 'deleteFolder', 'id']),
          10
        );
        // If the deleted folder was the folder we're currently routed
        // to, then route to home.
        if (deletedFolderId && deletedFolderId === folderId) {
          store.dispatch(routeTo('/app/zaps'));
        }
      },
      (action, store) => {
        // Reload zaps after deletion since zaps in the deleted
        // folder will have moved. If we don't reload zaps then
        // the user won't see the zaps from the deleted folder
        // until they reload the page.
        store.dispatch(FlowActions.loadZaps());
      },
    ],
  },
  updateFolder: {
    errorMessage: 'Couldn’t save folder.',
  },
  createFolderMembership: {
    errorMessage: 'Couldn’t share folder with member.',
  },
  deleteFolderMembership: {
    errorMessage: 'Couldn’t unshare folder with member.',
  },
  createFolderTeamship: {
    errorMessage: 'Couldn’t share folder with team.',
  },
  deleteFolderTeamship: {
    errorMessage: 'Couldn’t unshare folder with team.',
  },
  fullFolder: {
    errorMessage: 'Couldn’t load folder.',
  },
  createZapFromPublic: {
    errorMessage: 'Couldn’t create Zap.',
    sideEffects: [
      (action, store) => {
        const newZapId = _.get(action, ['data', 'createZapFromPublic', 'id']);
        if (newZapId) {
          store.dispatch(routeTo(`/app/editor/${newZapId}/intro`));
        }
      },
    ],
  },
};

// These operations can mirror each other.
operations.partialFolders = operations.fullFolders;

const temporaryFoldersMiddleware = store => next => action => {
  if (
    action &&
    action.type === ActionTypes.APOLLO_LINK_STATE_SYNC &&
    action.operationName in operations &&
    action.result
  ) {
    const { operationName, result } = action;
    const config = operations[operationName];

    if (result.errors) {
      const errors = _.isEmpty(result.errors)
        ? config.errorMessage
        : result.errors;
      store.dispatch(
        ToastNotification.actions.createFailure(
          errors,
          `notification-for-${operationName}`
        )
      );
    }

    (config.sideEffects || []).forEach(effect => {
      effect(action, store);
    });
  }

  return next(action);
};

export default temporaryFoldersMiddleware;
